/**
 * MONEY — docs/03 §3.7: the ledger, and since gasless funding the two relays.
 *
 * The four custodial ones are gone: `/wallet/deposit-address`,
 * `/wallet/deposits` and the two `/wallet/withdrawals` paths were deleted from
 * `openapi` in §F28j along with the vault they described. Settlement is
 * non-custodial — the USDC never leaves the user's own wallet — so there is no
 * address to deposit to and nothing to withdraw from. Funding is the user moving
 * their own money between USDC and USDU in their own wallet — directly, or by
 * signing and letting the venue send the transaction and pay its gas
 * (`relayDeposit`, `relayWithdraw`). The venue relays; it never holds.
 *
 * **The ledger survives, with a different job.** It is no longer the record a
 * user must trust between deposit and withdrawal — the chain is that, and always
 * was better evidence. What it answers is *why* each of the four balance
 * components moved, which the chain cannot say, and it is the only account of
 * `order_locked` at all: orders rest off-chain, so nothing on-chain reserves
 * that USDC.
 *
 * The old promise that it "reconciles to the penny against `/portfolio/balance`"
 * was retired with the same decision — it only ever proved the venue agreed with
 * itself. `/portfolio/balance` reconciles against Arbitrum now, which is
 * strictly stronger and is what exit criterion 3 asserts.
 *
 * **It does not poll.**
 */

import type { HttpClient, MutationOptions, RequestOptions } from '../client'
import type { OkBody, OpBody, Page, OpQuery } from '../types'

export function walletEndpoints(client: HttpClient) {
  return {
    /** Every balance-component change with a cause: `ORDER_LOCK`,
     *  `ORDER_UNLOCK`, `TRADE`, `FEE`, `REBATE`, `POSITION_MERGE`,
     *  `SETTLEMENT`, `VOID_REFUND`. Entries sharing a `journal_id` sum to
     *  exactly zero. */
    getLedger(query?: OpQuery<'getLedger'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'getLedger'>>>('/wallet/ledger', { ...options, query })
    },

    /** "Add funds" without gas (`backend/GASLESS-PLAN.md`): a signed USDC
     *  receive authorisation to USDU, which the venue sends and pays for.
     *  `202` once it is sent; the balance moves when it mines. */
    relayDeposit(body: OpBody<'relayDeposit'>, options?: MutationOptions) {
      return client.post<OkBody<'relayDeposit'>>('/chain/deposit', body, options)
    },

    /** "Withdraw" without gas: a signed `Withdraw`, sent by the venue, which
     *  pays the part in no market in the same transaction (`paid_now`) and
     *  sets the rest aside until its markets have settled (`ready_at_ms`,
     *  `null` when nothing waits), then finishes it (`docs/06` §F126). */
    relayWithdraw(body: OpBody<'relayWithdraw'>, options?: MutationOptions) {
      return client.post<OkBody<'relayWithdraw'>>('/chain/withdraw', body, options)
    },
  }
}

export type WalletEndpoints = ReturnType<typeof walletEndpoints>
